import { useParams, useNavigate, Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { getServiceBySlug } from './serviceData';
import { useTranslation } from '@/hooks/useTranslation';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import CustomCursor from '@/components/CustomCursor';
import BlogCard from '@/pages/blog/BlogCard';
import { Brain, Cpu, Code, Eye, Zap, Lightbulb, ArrowLeft, ArrowRight, type LucideIcon } from 'lucide-react';

const iconMap: Record<string, LucideIcon> = {
  brain: Brain, cpu: Cpu, code: Code, eye: Eye, zap: Zap, lightbulb: Lightbulb,
};

const ServicePage = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { language } = useTranslation();
  const service = slug ? getServiceBySlug(slug) : undefined;
  const [relatedPosts, setRelatedPosts] = useState<any[]>([]);

  useEffect(() => {
    if (!service) return;
    const fetchRelated = async () => {
      const { data } = await supabase
        .from('blog_posts')
        .select('id, slug, title_en, title_ro, excerpt_en, excerpt_ro, cover_image, category, published_at, reading_time_min, tags')
        .eq('status', 'published')
        .eq('category', service.relatedCategory)
        .order('published_at', { ascending: false })
        .limit(6);
      setRelatedPosts(data || []);
    };
    fetchRelated();
  }, [service]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!service) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen flex items-center justify-center" style={{ paddingTop: 120 }}>
          <div className="text-center">
            <h1 className="text-3xl font-bold mb-4" style={{ fontFamily: 'var(--f-display)', color: 'var(--ink)' }}>Service not found</h1>
            <button onClick={() => navigate('/')} className="text-[var(--amber)] font-semibold hover:underline">← Back to Home</button>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const Icon = iconMap[service.icon] || Brain;
  const baseUrl = 'https://add-individual-solutions.com';

  const schema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'CollectionPage',
        '@id': `${baseUrl}/services/${service.slug}#page`,
        name: service.title,
        description: service.metaDescription,
        url: `${baseUrl}/services/${service.slug}`,
        provider: { '@id': `${baseUrl}/#organization` },
        hasPart: relatedPosts.map((p) => ({
          '@type': 'Article',
          '@id': `${baseUrl}/blog/${p.slug}`,
          name: language === 'ro' ? (p.title_ro || p.title_en) : p.title_en,
        })),
      },
      {
        '@type': 'Service',
        serviceType: service.title,
        description: service.heroDescription,
        provider: { '@id': `${baseUrl}/#organization` },
        areaServed: [
          { '@type': 'Country', name: 'Cyprus' },
          { '@type': 'Place', name: 'Europe' },
        ],
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: baseUrl },
          { '@type': 'ListItem', position: 2, name: 'Services', item: `${baseUrl}/#services` },
          { '@type': 'ListItem', position: 3, name: service.title, item: `${baseUrl}/services/${service.slug}` },
        ],
      },
    ],
  };

  return (
    <>
      <CustomCursor />
      <Navbar />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />

      <main style={{ paddingTop: 100 }}>
        {/* Breadcrumb */}
        <div className="max-w-6xl mx-auto px-6 mb-6">
          <nav className="flex items-center gap-2 text-sm" style={{ color: 'var(--slate-light)', fontFamily: 'var(--f-mono)' }}>
            <Link to="/" className="hover:text-[var(--amber)] transition-colors">Home</Link>
            <span>/</span>
            <Link to="/#services" className="hover:text-[var(--amber)] transition-colors">Services</Link>
            <span>/</span>
            <span style={{ color: 'var(--ink)' }}>{service.title}</span>
          </nav>
        </div>

        {/* Hero */}
        <section className="max-w-6xl mx-auto px-6 pb-16">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center" style={{ background: 'var(--blue)', color: 'var(--white)' }}>
              <Icon size={28} />
            </div>
            <div className="flex flex-wrap gap-2">
              {service.techStack.map((t) => (
                <span key={t} className="px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider" style={{ background: 'var(--blue-light)', color: 'var(--blue)', fontFamily: 'var(--f-mono)' }}>{t}</span>
              ))}
            </div>
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6" style={{ fontFamily: 'var(--f-display)', color: 'var(--ink)' }}>{service.title}</h1>
          <p className="text-lg md:text-xl leading-relaxed max-w-3xl" style={{ color: 'var(--slate)' }}>{service.heroDescription}</p>
        </section>

        {/* Detail Sections */}
        <section className="max-w-6xl mx-auto px-6 pb-20">
          <div className="grid md:grid-cols-2 gap-8">
            {service.sections.map((sec, i) => (
              <div key={i} className="p-8 rounded-2xl" style={{ background: i % 2 === 0 ? 'var(--off-white)' : 'var(--white)', border: '1px solid var(--line)' }}>
                <h2 className="text-2xl font-bold mb-4" style={{ fontFamily: 'var(--f-display)', color: 'var(--blue)' }}>{sec.heading}</h2>
                <p className="leading-relaxed" style={{ color: 'var(--slate)', lineHeight: 1.8 }}>{sec.content}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Related Blog Posts */}
        {relatedPosts.length > 0 && (
          <section className="max-w-6xl mx-auto px-6 pb-20">
            <h2 className="text-3xl font-bold mb-8" style={{ fontFamily: 'var(--f-display)', color: 'var(--ink)' }}>Related Insights</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {relatedPosts.map((post) => (
                <BlogCard key={post.id} post={post} />
              ))}
            </div>
          </section>
        )}

        {/* CTA */}
        <section className="max-w-6xl mx-auto px-6 pb-24">
          <div className="rounded-2xl p-10 md:p-14 text-center" style={{ background: 'var(--blue)', color: 'var(--white)' }}>
            <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ fontFamily: 'var(--f-display)' }}>Ready to build your {service.title.toLowerCase()} solution?</h2>
            <p className="text-lg opacity-80 mb-8 max-w-2xl mx-auto">Let's discuss how we can engineer a system tailored to your business objectives.</p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/#contact" className="inline-flex items-center gap-2 px-8 py-4 rounded-full font-bold text-base transition-all hover:scale-105" style={{ background: 'var(--amber)', color: 'var(--ink)' }}>
                Start a Project <ArrowRight size={18} />
              </Link>
              <Link to="/" className="inline-flex items-center gap-2 px-8 py-4 rounded-full font-bold text-base transition-all hover:scale-105" style={{ background: 'rgba(255,255,255,0.15)', color: 'var(--white)' }}>
                <ArrowLeft size={18} /> Back to Home
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
};

export default ServicePage;
